import React, { useState } from "react";
import { Upload, message } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { bool, func, string, object, array } from "prop-types";
import { useUploadMedia } from "../../api/upload";
import { makeUniqueString, sanitizeForUrl } from "../../utils/stringUtils";
import {
  getThumbnailForImage,
  getThumbnailFromVideo,
} from "../../utils/cloudflareTransformUrl";
import { isVideoMimeType } from "../../utils/video";

const mimeTypesToExtensions = {
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/gif": ["gif"],
  "image/webp": ["webp"],
  "video/mp4": ["mp4"],
};

export default function ImageUpload({
  imageName,
  imageSizeValidation,
  errorMessage,
  altText,
  onUpload,
  defaultImageUrl,
  useThumbnailPreview = true,
  containerName,
  variants,
  mimeTypes = ["image/jpeg", "image/png", "image/webp"],
}) {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const beforeUpload = file => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split(".").pop();

      if (
        !mimeTypes.some(type => type === file.type) ||
        !mimeTypesToExtensions[file.type].includes(fileExtension)
      ) {
        message.error(
          `Invalid file type. Supported: ${mimeTypes
            .map(type => type.split("/").pop())
            .join(", ")}`
        );
        return reject();
      }

      // Skip aspect ratio validation for video files
      if (isVideoMimeType(file.type)) {
        return resolve();
      }

      const reader = new FileReader();

      //Read the contents of Image File.
      reader.readAsDataURL(file);
      reader.onload = function (e) {
        //Initiate the JavaScript Image object.
        const image = new Image();

        //Set the Base64 string return from FileReader as source.
        image.src = e.target.result;

        //Validate the File Height and Width.
        if (imageSizeValidation) {
          image.onload = function () {
            const { width, height } = this;
            if (imageSizeValidation(width, height)) {
              return resolve();
            }
            message.error(errorMessage);
            return reject();
          };
        } else {
          return resolve();
        }
      };
    });
  };

  const uploadMedia = useUploadMedia();

  const handleChange = info => {
    if (info.file.status === "uploading") {
      setImageUrl("");
      setLoading(true);
      return;
    }
    if (info.file.status === "done") {
      setLoading(false);
    }
  };

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const _uploadImage = async options => {
    const { file, onSuccess } = options;

    // If we were given a .gif, don't convert to .webp
    const { extension, mimeType } = getMetadataFromFile(file);

    let _variants = variants;
    // We don't process variants for gifs because sharp does not do a good job with
    // gif resizing. Instead, we just upload the result and return it as all variants.
    if (extension === "gif") {
      _variants = {};
    }

    // If the image name contains special characters (e.g. %), sanitize it for use in a URL
    // Else Cloudflare will not be able to generate a valid thumbnail
    const sanitizedImageName = sanitizeForUrl(imageName);

    // Cloudflare will cache an image by name. If we use the same name, it will display the first uploaded image
    // instead of the most recent one. To avoid this, we generate a unique name for each upload.
    // This is important for the thumbnail preview to work correctly.
    // We could append ?v=timestamp to the URL, but we would then need to ensure it is used consistently
    const uniqueFileName = makeUniqueString(sanitizedImageName, "upload");

    const urls = await uploadMedia.mutateAsync({
      file,
      fileName: uniqueFileName,
      containerName,
      variants: _variants,
      mimeType,
    });

    let displayUrl = urls.sourceMediaUrl;
    // If we want a thumbnail preview, generate it
    if (useThumbnailPreview) {
      // If the mime type is a video, we get a thumbnail from the video
      if (isVideoMimeType(mimeType)) {
        displayUrl = getThumbnailFromVideo(urls.sourceMediaUrl);
      } else if (variants && variants.thumbnail) {
        // If we have a thumbnail variant, use that
        displayUrl = urls.thumbnailMediaUrl;
      } else {
        // Otherwise, generate a thumbnail for the image from the source URL
        displayUrl = getThumbnailForImage(urls.sourceMediaUrl);
      }
    }

    setImageUrl(displayUrl);

    if (onUpload) {
      onUpload(urls);
    }
    onSuccess("ok");
  };

  // Accept takes both mime types and file extensions, so we make a big ugly list of all the
  // accepted types.
  const acceptStrings = `${mimeTypes.join(",")},${mimeTypes
    .flatMap(type => mimeTypesToExtensions[type])
    .join(",")}`;

  return (
    <Upload
      accept={acceptStrings}
      disabled={loading}
      listType="picture-card"
      showUploadList={false}
      beforeUpload={file =>
        beforeUpload(file, imageSizeValidation, errorMessage)
      }
      customRequest={_uploadImage}
      onChange={handleChange}
    >
      {defaultImageUrl || imageUrl ? (
        <img
          src={imageUrl || defaultImageUrl}
          alt={altText}
          style={{ width: "100%" }}
        />
      ) : (
        uploadButton
      )}
    </Upload>
  );
}

function getMetadataFromFile(file) {
  return {
    mimeType: file.type,
    extension: file.type.split("/").pop(),
  };
}

ImageUpload.propTypes = {
  imageName: string,
  onUpload: func,
  defaultImageUrl: string,
  imageSizeValidation: func,
  altText: string,
  errorMessage: string,
  useThumbnailPreview: bool,
  containerName: string,
  variants: object,
  mimeTypes: array,
};
